import "bootstrap/dist/css/bootstrap.min.css";

import { Header, Sidebar } from "components";
import React, { useState, useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import StickyBox from "react-sticky-box";
import "./App.css";

import { Login, Home } from "pages";

import { useAuth } from "utils/hooks";

import {
  AuthProvider,
  GnssApiProvider,
  UsersApiProvider,
  WebSocketProvider,
  FilterProvider,
  ToastProvider,
} from "utils/providers";

import { publicRoutes, sidebarConfig } from "routes";
import { RbacProvider } from "./utils/providers/RbacProvider";

/**
 * The main application component.
 *
 * It wraps all routes, providers, layout (Header, Sidebar), and handles conditional rendering
 * based on authentication and route protection.
 *
 * @component
 * @example
 * return (
 *   <App />
 * )
 */
function App() {
  const [displaySidebar, setDisplaySidebar] = useState(false);

  useEffect(() => {
    const runtimeTitle = window.RUNTIME_CONFIG?.APP?.TITLE || "ECHO Paltform";
    document.title = runtimeTitle;
  }, []);

  /**
   * Controls the visibility of the sidebar.
   * @param {string} token - Authentication token
   */
  const handleDisplaySidebar = (token) => {
    setDisplaySidebar(!!token);
  };

  /**
   * ProtectedRoute component wrapper.
   * Redirects to `/login` if the user is not authorized.
   *
   * @component
   * @param {Object} props
   * @param {React.ReactNode} props.children - Child components to render if authorized.
   * @returns {React.ReactNode}
   */
  const ProtectedRoute = ({ children }) => {
    const { isUserAuthorized } = useAuth();

    // Update sidebar state after render
    useEffect(() => {
      handleDisplaySidebar(isUserAuthorized);
    }, [isUserAuthorized]);

    if (!isUserAuthorized) {
      return <Navigate to="/login" replace />;
    }

    return children;
  };

  /**
   * PublicRoute component wrapper.
   * Ensures the sidebar is shown based on tenant access token.
   *
   * @component
   * @param {Object} props
   * @param {React.ReactNode} props.children - Child components to render.
   * @returns {React.ReactNode}
   */
  const PublicRoute = ({ children }) => {
    const { isUserAuthorized } = useAuth();

    handleDisplaySidebar(!!isUserAuthorized);
    return children;
  };

  useEffect(() => {
    const handleResize = () => {
      setCollapsed(window.innerWidth < 1200); // Bootstrap lg breakpoint
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [collapsed, setCollapsed] = useState(false);

  return (
    <>
      <WebSocketProvider>
        <AuthProvider>
          <UsersApiProvider>
            <GnssApiProvider>
              <FilterProvider>
                <RbacProvider>
                  {displaySidebar && <Header />}
                  <div
                    style={{
                      background: "#000",
                      width: "100%",
                      position: "relative",
                      // minWidth: 900,
                      display: "flex",
                      flex: 1,
                      alignItems: "flex-start",
                      marginTop: 0,
                      position: "absolute",
                      height: displaySidebar
                        ? "calc(100vh - 56px)"
                        : "calc(100vh)",
                      overflow: "auto",
                    }}
                  >
                    {displaySidebar && (
                      <StickyBox>
                        <Sidebar collapsed={collapsed} />
                      </StickyBox>
                    )}

                    <div
                      className="pageContent"
                      style={{
                        marginTop: "0px",
                        position: "relative",
                        width: displaySidebar
                          ? `calc(100vw - ${collapsed ? 80 : 250}px)`
                          : "100vw",
                      }}
                    >
                      <ToastProvider>
                        <Routes>
                          <Route index element={<Home></Home>} />

                          {publicRoutes.map((route) => (
                            <Route
                              key={route.route}
                              path={route.route}
                              element={
                                <PublicRoute>{route.component()}</PublicRoute>
                              }
                            />
                          ))}

                          {sidebarConfig.sections.map((section) =>
                            section.subsections
                              .filter(
                                (subsection) => subsection.isEnabled === true,
                              )
                              .map((subsection) => (
                                <Route
                                  exact
                                  key={subsection.route}
                                  path={subsection.route}
                                  element={
                                    <ProtectedRoute roles={subsection.roles}>
                                      {subsection.component()}
                                    </ProtectedRoute>
                                  }
                                />
                              )),
                          )}
                        </Routes>
                      </ToastProvider>
                    </div>
                  </div>
                </RbacProvider>
              </FilterProvider>
            </GnssApiProvider>
          </UsersApiProvider>
        </AuthProvider>
      </WebSocketProvider>
    </>
  );
}

export default App;
